import {differenceInCalendarDays, startOfDay} from 'date-fns'

import {spanForScheduleKind} from '../dispatch-timeline/job-schedule'
import type {JobScheduleKind, JobTimeSpan} from '../dispatch-timeline/types'
import {SLOTS_PER_DAY, WORK_DAY_START_HOUR} from '../dispatch-timeline/work-day'
import {parseApiDate, parseApiTime} from '../jobs/job-schedule-format'
import type {JobDetail} from '../jobs/types'

export type JobScheduleTimelineMapping = {
    scheduleKind: JobScheduleKind
    span: JobTimeSpan
}

export function timeToSlotIndex(time: string): number {
    const normalized = parseApiTime(time)
    if (!normalized) {
        return 0
    }

    const hours = Number(normalized.split(':')[0])
    const slot = hours - WORK_DAY_START_HOUR
    return Math.min(SLOTS_PER_DAY - 1, Math.max(0, slot))
}

export function mapJobScheduleToTimeline(
    job: Pick<JobDetail, 'startDate' | 'startTime' | 'endDate' | 'endTime'>,
    anchorDate: Date,
): JobScheduleTimelineMapping {
    const start = parseApiDate(job.startDate)
    const end = job.endDate ? parseApiDate(job.endDate) : start
    const dayOffset = differenceInCalendarDays(startOfDay(start), startOfDay(anchorDate))
    const isMultiDay = differenceInCalendarDays(end, start) > 0

    if (isMultiDay) {
        const daySpan = differenceInCalendarDays(end, start) + 1
        return {
            scheduleKind: 'two_day',
            span: {
                ...spanForScheduleKind('two_day', {dayOffset}),
                daySpan,
            },
        }
    }

    if (job.startTime && job.endTime) {
        const startSlotIndex = timeToSlotIndex(job.startTime)
        const endSlotIndex = timeToSlotIndex(job.endTime)
        return {
            scheduleKind: 'timed',
            span: {
                ...spanForScheduleKind('timed', {
                    dayOffset,
                    startSlotIndex,
                    endSlotIndex: Math.max(startSlotIndex, endSlotIndex),
                }),
                startClock: parseApiTime(job.startTime) ?? undefined,
            },
        }
    }

    // No end date (and no timed window) means the booking stays open — strip paints
    // busy from the start day onward, matching worker/equipment assignment peeks.
    if (job.endDate == null) {
        return {
            scheduleKind: 'one_day',
            span: {
                ...spanForScheduleKind('one_day', {dayOffset}),
                openEnded: true,
            },
        }
    }

    return {
        scheduleKind: 'one_day',
        span: spanForScheduleKind('one_day', {dayOffset}),
    }
}
